"use client";

import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import NadaLandingPage from "./nada";
import AtdLandingPage from "./atd";
import CadaLandingPage from "./cada";
import CadaFrLandingPage from "./cada-fr";
import { getStoredLocale, normalizeLocaleForContext } from "@/utils/localeCookie";

type Tenant = "nada" | "cada" | "atd";
type Locale = "en" | "fr";

function detectTenant(): Tenant {
    if (typeof window === "undefined") return "nada";

    const hostname = window.location.hostname;
    if (hostname.includes("cada")) {
        return "cada";
    } else if (hostname.includes("atd")) {
        return "atd";
    }
    return "nada";
}

function detectLocale(): Locale {
    if (typeof window === "undefined") return "en";

    const storedLocale = getStoredLocale();
    return storedLocale ? normalizeLocaleForContext(storedLocale) : "en";
}

export default function LandingPage() {
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [locale, setLocale] = useState<Locale>("en");
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
        setTenant(detectTenant());
        setLocale(detectLocale());

        // Listen for locale changes
        const handleLocaleChange = (e: CustomEvent<{ locale: string }>) => {
            setLocale(normalizeLocaleForContext(e.detail.locale));
        };

        window.addEventListener("localeChange", handleLocaleChange as EventListener);
        return () => window.removeEventListener("localeChange", handleLocaleChange as EventListener);
    }, []);

    // Show loading state during SSR or before client-side detection
    if (!isClient || tenant === null) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    bgcolor: "white",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // Route to the correct tenant/language page
    switch (tenant) {
        case "cada":
            return locale === "fr" ? <CadaFrLandingPage /> : <CadaLandingPage />;
        case "atd":
            return <AtdLandingPage />;
        case "nada":
        default:
            return <NadaLandingPage />;
    }
}
